/**
 * PIEZAS PEQUEÑAS QUE SE REPITEN
 * =============================================================================
 *   · Revelar    el contenido aparece al entrar en pantalla
 *   · AvisoPendiente  el cartel que dice qué datos faltan
 *   · Bloque     una sección con fotografía de fondo
 */

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { datosPendientes } from '../config';
import { Fondo, type Escena } from './Fondo';
import { Icono } from './Iconos';
import { useIdioma } from '../i18n/useIdioma';

// =============================================================================
// REVELAR
// =============================================================================

/**
 * Hace que su contenido aparezca con un desplazamiento suave al entrar en
 * pantalla.
 *
 * -----------------------------------------------------------------------------
 * SI EL NAVEGADOR NO EJECUTA JAVASCRIPT, SE VE IGUAL
 * -----------------------------------------------------------------------------
 * La clase que esconde el contenido se pone con JavaScript, no en el HTML. Si el
 * script no corre, el contenido nunca se esconde y la página se ve completa. Es al
 * revés de como se hace normalmente, y es a propósito: una página que depende de
 * JavaScript para mostrar su texto es una página que a veces se ve en blanco.
 *
 * Y si el sistema pide menos movimiento —hay gente a la que le causa mareo— la
 * hoja de estilos lo desactiva sola.
 */
export function Revelar({
  children,
  retardo = 0,
  className,
}: {
  children: ReactNode;
  retardo?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Si el navegador no sabe observar, se muestra directamente.
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observador = new IntersectionObserver(
      (entradas) => {
        for (const e of entradas) {
          if (e.isIntersecting) {
            setVisible(true);
            // Una vez que apareció, deja de observarse.
            observador.unobserve(e.target);
          }
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`revelar ${visible ? 'revelar--visible' : ''} ${className ?? ''}`}
      style={retardo ? { transitionDelay: `${retardo}ms` } : undefined}
    >
      {children}
    </div>
  );
}

// =============================================================================
// EL AVISO DE DATOS PENDIENTES
// =============================================================================

/**
 * El cartel que dice qué datos oficiales faltan.
 *
 * -----------------------------------------------------------------------------
 * POR QUÉ EXISTE Y POR QUÉ SE PUEDE CERRAR
 * -----------------------------------------------------------------------------
 * Esta página está terminada, pero hay datos que no me diste y que no puedo
 * inventar: el correo, los horarios, los perfiles de redes. Sin este aviso, quien
 * la ve no sabría que faltan, y algunos enlaces no llevarían a ningún lado sin
 * explicar por qué.
 *
 * Se puede cerrar porque quien ya lo leyó no necesita verlo cada vez. Y al
 * cerrarlo se acuerda, así que no vuelve a aparecer al recargar.
 */
export function AvisoPendiente() {
  const { textos } = useIdioma();
  const [cerrado, setCerrado] = useState(true);

  // Se comprueba al montar, porque `localStorage` no existe mientras se genera el
  // HTML.
  useEffect(() => {
    try {
      setCerrado(localStorage.getItem('msoccer-aviso-pendientes') === 'visto');
    } catch {
      setCerrado(false);
    }
  }, []);

  const faltan = datosPendientes();
  if (cerrado || faltan.length === 0) return null;

  const cerrar = () => {
    setCerrado(true);
    try {
      localStorage.setItem('msoccer-aviso-pendientes', 'visto');
    } catch {
      // Sin permiso para guardar: se cierra igual en esta visita.
    }
  };

  return (
    <div className="aviso aviso--aviso" role="status" style={{ marginBottom: 32 }}>
      <span className="aviso__icono">
        <Icono nombre="lista" tam={20} />
      </span>
      <span style={{ flex: 1 }}>
        <strong className="aviso__titulo">{textos.pendientes.titulo}</strong>
        {textos.pendientes.texto}

        <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 13 }}>
          {faltan.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>

        <p style={{ marginTop: 10, fontSize: 12, opacity: 0.75 }}>
          {textos.pendientes.verArchivo}
        </p>

        <button
          type="button"
          className="boton boton--marco"
          style={{ minHeight: 40, marginTop: 14 }}
          onClick={cerrar}
        >
          {textos.pendientes.cerrar}
        </button>
      </span>
    </div>
  );
}

// =============================================================================
// BLOQUE DE SECCIÓN
// =============================================================================

/**
 * Una sección con su fotografía de fondo y su velo oscuro.
 *
 * Se hace como componente porque cuatro secciones comparten exactamente la misma
 * estructura. Repetirla cuatro veces sería la forma más fácil de que con el tiempo
 * se desalinearan.
 */
export function BloqueFoto({
  id,
  foto,
  escena,
  posicion,
  children,
}: {
  id: string;
  foto: Parameters<typeof Fondo>[0]['foto'];
  escena: Escena;
  posicion?: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="seccion seccion--foto">
      <Fondo className="seccion__fondo" foto={foto} escena={escena} posicion={posicion} />
      <div className="seccion__velo" aria-hidden="true" />
      <div className="caja seccion__contenido">{children}</div>
    </section>
  );
}

/** Una sección normal, sin fotografía de fondo. */
export function Bloque({
  id,
  children,
  className,
}: {
  id: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section id={id} className={`seccion ${className ?? ''}`}>
      <div className="caja">{children}</div>
    </section>
  );
}
